import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { TailSpin } from "react-loader-spinner";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs/tabs.tsx";
import { Select } from "../../components/ui/select/select.tsx";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { useHolidays } from "../../hooks/use-holidays.ts";
import { REGION_TABS, REGION_LABELS } from "../../components/features/holidays/constants.ts";
import type { HolidayRegion } from "../../components/features/holidays/types.ts";
import { HolidaysHero } from "../../components/features/holidays/components/holidays-hero/holidays-hero.tsx";
import { HolidaysStats } from "../../components/features/holidays/components/holidays-stats/holidays-stats.tsx";
import { HolidaysTable } from "../../components/features/holidays/components/holidays-table/holidays-table.tsx";
import { generateYearAll } from "../../../utils/helpers.js";

import styles from "./holidays.module.css";

const SQE_REGION: HolidayRegion = "SQE";
const SQE_EMPLOYEE_TYPES = new Set(["SQE", "SQELABS"]);
const SUPERADMIN_ROLE = "SUPERADMIN";
const HR_ROLE = "HR";

type StoredUserData = {
  empType?: string;
  employeeType?: string;
  type?: string;
  role?: string;
  roleName?: string;
  userRole?: string;
  roles?: string[];
  location?: string;
  employeeLocation?: string;
  personalDetail?: {
    empType?: string;
    employeeType?: string;
    type?: string;
    location?: string;
    employeeLocation?: string;
  };
};

function normalizeUserValue(value: unknown): string {
  return String(value ?? "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();
}

function getLoggedInUserData(): StoredUserData | null {
  const userData = localStorage.getItem("userData");
  if (!userData) return null;

  try {
    return JSON.parse(userData) as StoredUserData;
  } catch (error) {
    console.error("Failed to parse user data from localStorage:", error);
    return null;
  }
}

function getLoggedInEmployeeTypes(): string[] {
  const parsed = getLoggedInUserData();
  if (!parsed) return [];

  return [
    parsed.empType,
    parsed.employeeType,
    parsed.type,
    parsed.personalDetail?.empType,
    parsed.personalDetail?.employeeType,
    parsed.personalDetail?.type,
  ].filter(
    (value): value is string =>
      typeof value === "string" && value.trim() !== "",
  );
}

function isSQELabsEmployee(): boolean {
  return getLoggedInEmployeeTypes().some((employeeType) =>
    SQE_EMPLOYEE_TYPES.has(normalizeUserValue(employeeType)),
  );
}

function getLoggedInRoles(): string[] {
  const parsed = getLoggedInUserData();
  if (!parsed) return [];

  return [
    ...(Array.isArray(parsed.roles) ? parsed.roles : []),
    parsed.role,
    parsed.roleName,
    parsed.userRole,
  ].filter(
    (value): value is string =>
      typeof value === "string" && value.trim() !== "",
  );
}

function hasFullRegionAccess(): boolean {
  return getLoggedInRoles().some(
    (role) => [SUPERADMIN_ROLE, HR_ROLE].includes(normalizeUserValue(role)),
  );
}

function getLoggedInLocation(): string {
  const parsed = getLoggedInUserData();
  return normalizeUserValue(
    parsed?.location ??
      parsed?.employeeLocation ??
      parsed?.personalDetail?.location ??
      parsed?.personalDetail?.employeeLocation,
  );
}

function parseHolidayDateMs(dateStr: string): number {
  const t = Date.parse(dateStr);
  if (Number.isFinite(t)) return t;
  const d = new Date(dateStr);
  return Number.isFinite(d.getTime()) ? d.getTime() : 0;
}

function filterByTab(rows: import("../../components/features/holidays/types.ts").Holiday[], tab: HolidayRegion) {
  return rows
    .filter((h) => h.region === tab)
    .sort((a, b) => parseHolidayDateMs(a.date) - parseHolidayDateMs(b.date));
}

function filterForSQELabs(rows: import("../../components/features/holidays/types.ts").Holiday[]) {
  return rows
    .filter((h) => h.region === SQE_REGION)
    .sort((a, b) => parseHolidayDateMs(a.date) - parseHolidayDateMs(b.date));
}

export default function HolidaysPage() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [regionTab, setRegionTab] = useState<HolidayRegion>("INDIA");
  const { holidays, loading, error } = useHolidays(year);
  const isSQEEmployee = useMemo(isSQELabsEmployee, []);
  const hasFullHolidayView = useMemo(hasFullRegionAccess, []);
  const loggedInLocation = useMemo(getLoggedInLocation, []);
  const isLocationRestricted = !hasFullHolidayView && ["CCIINDIA", "CCIUS"].includes(loggedInLocation);
  const isSQERestrictedView = isSQEEmployee && !hasFullHolidayView;
  const visibleRegionTabs = useMemo(() => {
    const nonGlobalRegionTabs = REGION_TABS.filter((tab) => tab.id !== "GLOBAL");
    if (isSQERestrictedView) {
      return nonGlobalRegionTabs.filter((tab) => tab.id === SQE_REGION);
    }
    if (isLocationRestricted) {
      return nonGlobalRegionTabs.filter((tab) => tab.id === "INDIA" || tab.id === "US");
    }
    return nonGlobalRegionTabs;
  }, [isLocationRestricted, isSQERestrictedView]);

  const yearOptions = useMemo(
    () =>
      generateYearAll(2024).map((y: number) => ({
        value: String(y),
        label: String(y),
      })),
    [],
  );

  const filteredRows = useMemo(
    () =>
      isSQERestrictedView
        ? filterForSQELabs(holidays)
        : filterByTab(holidays, regionTab),
    [holidays, regionTab, isSQERestrictedView],
  );

  const emptyMessage = `No holidays for ${year} in ${REGION_LABELS[regionTab]}.`;

  useEffect(() => {
    if (!visibleRegionTabs.some((tab) => tab.id === regionTab)) {
      setRegionTab(visibleRegionTabs[0]?.id ?? "INDIA");
    }
  }, [regionTab, visibleRegionTabs]);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Home", path: "/dashboard" },
      { label: "Holidays" },
    ]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  return (
    <PageShell>
      {loading && (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="loading-holidays"
              visible
            />
          </div>
        </div>
      )}


      <HolidaysHero />
      {!isSQERestrictedView && (
        <HolidaysStats
          holidays={holidays}
          visibleRegions={isLocationRestricted ? ["INDIA", "US"] : undefined}
        />
      )}

      {error ? (
        <p className={clsx("body-sm", styles.error)} role="alert">
          {error}
        </p>
      ) : null}

      <Card className={styles.tableCard} padding="5xl">
        <div className={styles.tableCardHeader}>
          <div className={styles.headerRow}>
            <h2 className={clsx("heading-sm", styles.tableTitle)}>
              Upcoming Holidays
            </h2>
            <div className={styles.yearSlot}>
              <Select
                id="holidays-year"
                placeholder="Year"
                value={String(year)}
                onChange={(v) => setYear(Number(v))}
                options={yearOptions}
              />
            </div>
          </div>
        </div>

        <Tabs value={regionTab} onValueChange={(v) => setRegionTab(v as HolidayRegion)}>
          <TabsList className={styles.tabsList}>
            {visibleRegionTabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id} className={styles.tabTrigger}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={regionTab} className={styles.tabPanel}>
            <HolidaysTable rows={filteredRows} emptyMessage={emptyMessage} />
          </TabsContent>
        </Tabs>
      </Card>
    </PageShell>
  );
}
